var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { Sutl } from '../../../sutl';
import { CommandSocketInitializer } from './framework/CommandSocketInitializer';
import { Timeouts } from './framework/Timeouts';
export var Resource;
(function (Resource) {
    Resource[Resource["SpeedDial"] = 1000] = "SpeedDial";
    Resource[Resource["NumberDialed"] = 1002] = "NumberDialed";
    Resource[Resource["CallerId"] = 1003] = "CallerId";
    Resource[Resource["NumberToDial"] = 1004] = "NumberToDial";
    Resource[Resource["CallStatus"] = 1005] = "CallStatus";
})(Resource || (Resource = {}));
const telephonyControlPoints = [
    { key: 'callEnd', callTypes: ['voip'], isPushRequired: false },
    { key: 'connectDisconnect', callTypes: ['pstn'], isPushRequired: false },
    { key: 'connectedLed', callTypes: ['pstn'], isPushRequired: true },
    { key: 'keypad0', callTypes: ['pstn', 'voip'], isPushRequired: false },
    { key: 'keypad1', callTypes: ['pstn', 'voip'], isPushRequired: false },
    { key: 'keypad2', callTypes: ['pstn', 'voip'], isPushRequired: false },
    { key: 'keypad3', callTypes: ['pstn', 'voip'], isPushRequired: false },
    { key: 'keypad4', callTypes: ['pstn', 'voip'], isPushRequired: false },
    { key: 'keypad5', callTypes: ['pstn', 'voip'], isPushRequired: false },
    { key: 'keypad6', callTypes: ['pstn', 'voip'], isPushRequired: false },
    { key: 'keypad7', callTypes: ['pstn', 'voip'], isPushRequired: false },
    { key: 'keypad8', callTypes: ['pstn', 'voip'], isPushRequired: false },
    { key: 'keypad9', callTypes: ['pstn', 'voip'], isPushRequired: false },
    { key: 'keypadPound', callTypes: ['pstn', 'voip'], isPushRequired: false },
    { key: 'keypadStar', callTypes: ['pstn', 'voip'], isPushRequired: false },
    { key: 'receiveGain', callTypes: ['pstn', 'voip'], isOptional: true, isPushRequired: true },
    { key: 'reject', callTypes: ['voip'], isPushRequired: false },
    { key: 'ringingLed', callTypes: ['pstn'], isPushRequired: true },
    { key: 'speedDial', callTypes: ['pstn'], isPushRequired: false },
];
function isPresetMuteConfig(cfg) {
    return typeof cfg !== 'number' && 'mutePreset' in cfg;
}
export class Radius {
    constructor(socket, state, log, framework) {
        this.socket = socket;
        this.state = state;
        this.log = log;
        this.framework = framework;
        // other class properties and constructor
        this.isDetectionNeeded = true;
        this.mutePresets = {};
        this.nonPushControllers = [];
        this.pushControllers = [];
        this.pushControllersMismatchCount = 0;
        this.telephonyChannels = [];
        this.timeouts = new Timeouts();
        this.unmutePresets = {};
        // controls
        this.detected = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                if (this.isDetectionNeeded) {
                    this.pushControllers.length = 0;
                    // Get push controllers by sending a GPU followed by a NOP. The GPU causes the
                    // device to send the push enabled controller numbers, each followed by carriage
                    // return. There is no indication when the device is done sending push controllers,
                    // so we wait a bit and send a NOP. The ACK from the NOP tells us we're done.
                    const id = this.socket.registerAsync(/^0*(\d+)$/, match => {
                        this.addController(this.pushControllers, { number: parseInt(match[1], 10) });
                    });
                    try {
                        yield this.socket.sendAsync('GPU', tags);
                        yield this.timeouts.delay(500);
                        yield this.socket.send('NOP', /^ACK$/, tags);
                    }
                    finally {
                        this.socket.unregisterAsync(id);
                    }
                    // Now, query each of the push controllers to see if they are queryable.
                    yield this.pushControllers.reduce((promise, controller) => __awaiter(this, void 0, void 0, function* () {
                        yield promise;
                        const canGet = yield this.canGet(controller.number, tags);
                        if (canGet)
                            controller.canGet = true;
                    }), Promise.resolve());
                    // To detect telephony channels, we try to set the last (20th) speed dial number for
                    // a range of units, cards, and channels. If we get an ACK, we know it's a telephony
                    // channel.
                    const cardSpecs = [];
                    for (let unit = 1; unit <= 10; unit++) {
                        for (let card = 0; card < 4; card++) {
                            for (let line = 0; line < 2; line++) {
                                cardSpecs.push([unit, card, line]);
                            }
                        }
                    }
                    yield cardSpecs.reduce((promise, spec) => __awaiter(this, void 0, void 0, function* () {
                        yield promise;
                        const [unit, card, line] = spec;
                        const match = yield this.socket.send(`SSYSS ${unit}.${Resource.SpeedDial}.19.${card}.${line}=2836`, /^(ACK|NAK)$/, tags);
                        if (match[1] === 'ACK')
                            this.addTelephonyChannel(unit, card, line);
                    }), Promise.resolve());
                    this.isDetectionNeeded = false;
                }
                return this.pushControllers.map(controller => ({
                    controllerNumber: controller.number,
                    filters: {
                        mics: controller.canGet,
                        micsMuteStatus: controller.canGet,
                        amplifier: controller.canGet,
                        assistiveListeningMute: controller.canGet,
                        assistiveListeningMuteStatus: controller.canGet,
                        assistiveListeningVolume: controller.canGet,
                        audioInputMute: controller.canGet,
                        audioInputMuteStatus: controller.canGet,
                        audioInputVolume: controller.canGet,
                        overflowRoomMute: controller.canGet,
                        overflowRoomMuteStatus: controller.canGet,
                        overflowRoomVolume: controller.canGet,
                        preset: false,
                        soundReinforcementMute: controller.canGet,
                        soundReinforcementMuteStatus: controller.canGet,
                        soundReinforcementVolume: controller.canGet,
                        telephony: true,
                        receiveGain: controller.canGet,
                    },
                }));
            }),
        };
        this.firmwareVersion = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                // The output of the version command, "$v V", ends in a ">" instead of a carriage
                // return -- probably because we used verbose mode. We send an extra NOP command to make
                // sure we get past it so that it doesn't mess up the processing of the next command.
                const match = yield this.socket.send(`$v V`, /^firmware\s+version:\s+v?([\d\.]+)/i, tags);
                yield this.socket.send(`NOP`, /ACK/, tags);
                return match[1];
            }),
        };
        this.mute = {
            get: (key, tags) => __awaiter(this, void 0, void 0, function* () {
                var _a;
                const cn = parseInt(key, 10);
                const value = yield this.getControllerValue(cn, tags);
                if (value === undefined)
                    return (_a = this.state.mutes.values[key]) !== null && _a !== void 0 ? _a : false;
                const controller = this.getController(this.pushControllers, cn);
                return (controller === null || controller === void 0 ? void 0 : controller.filter) === 'amplifier' ? value <= 26526 : value !== 0;
            }),
            set: (key, value, tags) => __awaiter(this, void 0, void 0, function* () {
                var _b, _c, _d;
                if (this.mutePresets[key]) {
                    const presetNumber = value ? this.mutePresets[key] : this.unmutePresets[key];
                    const match = yield this.socket.send(`LP ${presetNumber}`, /^(ACK|NAK)$/, tags);
                    return match[1] === 'ACK' ? value : (_b = this.state.mutes.values[key]) !== null && _b !== void 0 ? _b : false;
                }
                const controller = this.getController(this.pushControllers, parseInt(key, 10));
                let controllerValue = 0;
                if ((controller === null || controller === void 0 ? void 0 : controller.filter) === 'amplifier') {
                    controllerValue = value ? 0 : (_c = this.state.volumes.values[key]) !== null && _c !== void 0 ? _c : 0;
                }
                else {
                    controllerValue = value ? 65535 : 0;
                }
                const match = yield this.socket.send(`CSQ ${key} ${controllerValue}`, /^(ACK|NAK)$/, tags);
                return match[1] === 'ACK' ? value : (_d = this.state.mutes.values[key]) !== null && _d !== void 0 ? _d : false;
            }),
            getKey: this.getKey.bind(this),
            validate: this.validate.bind(this),
            asyncHandlers: [
                {
                    match: (cmd) => {
                        const match = /^#0*(\d+)=(\d+)$/.exec(cmd);
                        if (!match)
                            return undefined;
                        const cn = parseInt(match[1], 10);
                        const controller = this.getController(this.pushControllers, cn);
                        return (controller === null || controller === void 0 ? void 0 : controller.isMute)
                            ? [controller, parseInt(match[2], 10) !== 0]
                            : undefined;
                    },
                    callback: (match) => {
                        const [controller, value] = match;
                        this.state.mutes.update(controller.number.toString(), value);
                    },
                },
            ],
        };
        this.reboot = {
            set: () => __awaiter(this, void 0, void 0, function* () {
                // Hardwired for Randall Paulson Associates to a momentary button in the Symetrix
                // config file.
                yield this.socket.send(`CS 11 65535`, /^(ACK|NAK)$/);
            }),
        };
        this.telephony = {
            answer: (key) => __awaiter(this, void 0, void 0, function* () {
                var _e;
                const config = (_e = this.getTelephonyChannel(key)) === null || _e === void 0 ? void 0 : _e.config;
                if (config === undefined) {
                    this.log.error(`no telephony channel for key ${key}`);
                    return false;
                }
                const cn = config.callType === 'pstn' ? config.connectDisconnect : config.callEnd;
                const match = yield this.socket.send(`CSQ ${cn} 65535`, /^(ACK|NAK)$/);
                return match[1] === 'ACK';
            }),
            call: (value) => __awaiter(this, void 0, void 0, function* () {
                const tc = this.getAvailableTelephonyChannel(value.callType);
                const config = tc === null || tc === void 0 ? void 0 : tc.config;
                if (tc === undefined || config === undefined) {
                    this.log.error(`no available ${value.callType} call interfaces`);
                    return undefined;
                }
                tc.numberDialed = value.dialString;
                if (config.callType === 'voip') {
                    const key = `${config.unit}.${Resource.NumberToDial}.0.${config.card}.${config.line}`;
                    yield this.socket.send(`SSYSS ${key}=${value.dialString}`, /^(ACK|NAK)$/);
                    yield this.socket.send(`CS ${config.callEnd} 65535`, /^(ACK|NAK)$/);
                }
                else {
                    if (config.speedDial) {
                        // New dialing behavior (SNTY-1381).
                        const key = `${config.unit}.${Resource.SpeedDial}.19.${config.card}.${config.line}`;
                        yield this.socket.send(`SSYSS ${key}=${value.dialString}`, /^(ACK|NAK)$/);
                        yield this.socket.send(`CSQ ${config.speedDial} 65535`, /^(ACK|NAK)$/);
                    }
                    else {
                        // Fallback to old dialing behavior if no speedDial in config.
                        yield this.socket.send(`CSQ ${config.connectDisconnect} 65535`, /^(ACK|NAK)$/);
                        yield this.timeouts.delay(3000);
                        yield this.dialDigits(config, value.dialString);
                    }
                }
                return this.getTelephonyKey(config);
            }),
            dial: (key, value) => __awaiter(this, void 0, void 0, function* () {
                var _f;
                const config = (_f = this.getTelephonyChannel(key)) === null || _f === void 0 ? void 0 : _f.config;
                if (config === undefined) {
                    this.log.error(`no telephony channel for key ${key}`);
                    return value;
                }
                yield this.dialDigits(config, value);
                return value;
            }),
            hangup: (key) => __awaiter(this, void 0, void 0, function* () {
                var _g;
                const config = (_g = this.getTelephonyChannel(key)) === null || _g === void 0 ? void 0 : _g.config;
                if (config === undefined) {
                    this.log.error(`no telephony channel for key ${key}`);
                    return false;
                }
                const cn = config.callType === 'pstn' ? config.connectDisconnect : config.callEnd;
                const match = yield this.socket.send(`CSQ ${cn} 65535`, /^(ACK|NAK)$/);
                return match[1] === 'ACK';
            }),
            receiveGainRange: { max: 65535, min: 46811, round: 1 },
            reject: (key) => __awaiter(this, void 0, void 0, function* () {
                var _h;
                const config = (_h = this.getTelephonyChannel(key)) === null || _h === void 0 ? void 0 : _h.config;
                if (config === undefined) {
                    this.log.error(`no telephony channel for key ${key}`);
                    return false;
                }
                if (config.callType === 'pstn') {
                    const cn = config.connectDisconnect;
                    let match = yield this.socket.send(`CSQ ${cn} 65535`, /^(ACK|NAK)$/);
                    if (match[1] === 'NAK')
                        this.log.error(`reject failed`);
                    yield this.timeouts.delay(500);
                    match = yield this.socket.send(`CSQ ${cn} 65535`, /^(ACK|NAK)$/);
                    if (match[1] === 'NAK')
                        this.log.error(`reject failed`);
                    return true;
                }
                else {
                    const match = yield this.socket.send(`CSQ ${config.reject} 65535`, /^(ACK|NAK)$/);
                    return match[1] === 'ACK';
                }
            }),
            setReceiveGain: (key, value, tags) => __awaiter(this, void 0, void 0, function* () {
                var _j, _k;
                const config = (_j = this.getTelephonyChannel(key)) === null || _j === void 0 ? void 0 : _j.config;
                if (config === undefined) {
                    this.log.error(`no telephony channel for key ${key}`);
                    return value;
                }
                const cn = (_k = config.receiveGain) !== null && _k !== void 0 ? _k : 0;
                const match = yield this.socket.send(`CSQ ${cn} ${value}`, /^(ACK|NAK)$/, tags);
                return match[1] === 'ACK' ? value : 0;
            }),
            getCalls: () => __awaiter(this, void 0, void 0, function* () {
                return Object.fromEntries(this.telephonyChannels
                    .filter(tc => tc.isValid && tc.config)
                    .map(tc => {
                    var _a, _b, _c;
                    return [
                        this.getTelephonyKey(tc.config),
                        this.state.calls.addMissing(Object.assign({ callType: (_b = (_a = tc.config) === null || _a === void 0 ? void 0 : _a.callType) !== null && _b !== void 0 ? _b : 'pstn', canDial: true }, (((_c = tc.config) === null || _c === void 0 ? void 0 : _c.receiveGain) ? { receiveGain: 0 } : undefined))),
                    ];
                }));
            }),
            getMaxCalls: () => __awaiter(this, void 0, void 0, function* () {
                return this.telephonyChannels.reduce((maxCalls, tc) => {
                    var _a;
                    if (tc.isValid && tc.config) {
                        const callType = tc.config.callType;
                        maxCalls[callType] = ((_a = maxCalls[callType]) !== null && _a !== void 0 ? _a : 0) + 1;
                    }
                    return maxCalls;
                }, {});
            }),
            getKey: this.getTelephonyKey.bind(this),
            validate: (config, info) => __awaiter(this, void 0, void 0, function* () {
                const result = Object.fromEntries(yield telephonyControlPoints
                    .filter(cp => !cp.isOptional && cp.callTypes.includes(config.callType))
                    .reduce((promise, cp) => __awaiter(this, void 0, void 0, function* () {
                    yield promise;
                    const results = yield promise;
                    results.push([
                        cp.key,
                        yield this.validateTelephonyControlPoint(config, cp),
                    ]);
                    return results;
                }), Promise.resolve([])));
                const chan = this.getTelephonyChannel(config.unit, config.card, config.line);
                if (chan !== undefined) {
                    chan.config = config;
                    chan.isValid = true;
                }
                const channelResult = chan === undefined ? 'missing' : 'ok';
                result.unit = channelResult;
                result.card = channelResult;
                result.line = channelResult;
                if (config.receiveGain !== undefined) {
                    const controller = this.getController(this.pushControllers, config.receiveGain);
                    result.receiveGain =
                        controller === undefined
                            ? 'missing'
                            : controller.canGet
                                ? 'ok'
                                : 'incompatible';
                }
                return result;
            }),
            asyncHandlers: [
                {
                    // push controls: ringing (pstn), connnected (pstn), and receive gain (both)
                    match: (cmd) => {
                        const match = /^#0*(\d+)=(\d+)$/.exec(cmd);
                        if (!match)
                            return undefined;
                        const cn = parseInt(match[1], 10);
                        const controller = this.getController(this.pushControllers, cn);
                        return (controller === null || controller === void 0 ? void 0 : controller.filter) === 'telephony'
                            ? [controller, parseInt(match[2], 10)]
                            : undefined;
                    },
                    callback: (match) => {
                        const [controller, value] = match;
                        this.processCallControl(controller, value);
                    },
                },
                {
                    // push system strings: caller id and voip status
                    regExp: /^(\d+)\.(\d+)\.(\d+)\.(\d+)\.(\d+)=(.+)/,
                    callback: (match) => {
                        this.processCallString(match[1], match[2], match[4], match[5], match[6]);
                    },
                },
            ],
        };
        this.volume = {
            get: (key, tags) => __awaiter(this, void 0, void 0, function* () {
                var _l;
                const cn = parseInt(key, 10);
                const value = yield this.getControllerValue(cn, tags);
                return (_l = value !== null && value !== void 0 ? value : this.state.volumes.values[key]) !== null && _l !== void 0 ? _l : 0;
            }),
            set: (key, value, tags) => __awaiter(this, void 0, void 0, function* () {
                var _m;
                const cn = parseInt(key, 10);
                const controller = this.getController(this.pushControllers, cn);
                if ((controller === null || controller === void 0 ? void 0 : controller.filter) === 'amplifier')
                    this.state.mutes.update(key, value <= 26526);
                const match = yield this.socket.send(`CSQ ${key} ${value}`, /^(ACK|NAK)$/, tags);
                return match[1] === 'ACK' ? value : (_m = this.state.volumes.values[key]) !== null && _m !== void 0 ? _m : 0;
            }),
            getKey: this.getKey.bind(this),
            range: (config, info) => __awaiter(this, void 0, void 0, function* () {
                if (info.volumeType === 'trim')
                    return { max: 65535, min: 46811, round: 1 };
                return { max: 65535, min: 26526, round: 1 };
            }),
            validate: this.validate.bind(this),
            asyncHandlers: [
                {
                    match: (cmd) => {
                        const match = /^#0*(\d+)=(\d+)$/.exec(cmd);
                        if (!match)
                            return undefined;
                        const cn = parseInt(match[1], 10);
                        const controller = this.getController(this.pushControllers, cn);
                        return (controller === null || controller === void 0 ? void 0 : controller.isVolume) ? [controller, parseInt(match[2], 10)] : undefined;
                    },
                    callback: (match) => {
                        const [controller, value] = match;
                        this.state.volumes.update(controller.number.toString(), value);
                        if (controller.filter === 'amplifier') {
                            this.state.mutes.update(controller.number.toString(), value <= 26526);
                        }
                    },
                },
            ],
        };
        // polling
        this.polls = [
            {
                intervalMs: 4000,
                function: () => __awaiter(this, void 0, void 0, function* () {
                    let newPushControllers = [];
                    // Query the push controllers to detect if a new config has been uploaded to the
                    // Radius, then go back to the configuring phase if it has. We have seen the Radius
                    // incorrectly report controller numbers on occasion, so we make sure the results
                    // are consistent for three queries before taking action.
                    const id = this.socket.registerAsync(/^0*(\d+)$/, match => {
                        const cn = parseInt(match[1], 10);
                        if (!newPushControllers.includes(cn))
                            newPushControllers.push(cn);
                    });
                    // It is important to wrap the next two commands in a try block because if they are
                    // cancelled (due to the poll being removed when another command is executed), then
                    // we have to make sure to unregister the async handler.
                    try {
                        yield this.socket.sendAsync('GPU', ['poll']);
                        // We don't use NOP/ACK here to confirm the end of the GPU because other
                        // commands, e.g. mute and volume, can be happening asynchronously, and their
                        // ACKs can get confused with this one. So, we use the RI command instead.
                        yield this.socket.send(`RI`, /^\d+\.\d+\.\d+\.\d+$/i, ['poll']);
                    }
                    catch (error) {
                        if (error.code === 'sendTimeout') {
                            this.log.warning(`timeout waiting for poll response`);
                        }
                        else if (error.code === 'commandRemoved') {
                            this.log.info(`polling cancelled`);
                        }
                        else {
                            this.log.warning(`unexpected error while polling: ${error}`);
                        }
                        return;
                    }
                    finally {
                        this.socket.unregisterAsync(id);
                    }
                    newPushControllers = newPushControllers.sort();
                    const oldPushControllers = this.pushControllers.map(c => c.number).sort();
                    if (!Sutl.objEquals(newPushControllers, oldPushControllers)) {
                        this.log.info(`push controllers are different`);
                        this.log.info(`    old: ${JSON.stringify(oldPushControllers)}`);
                        this.log.info(`    new: ${JSON.stringify(newPushControllers)}`);
                        if (++this.pushControllersMismatchCount >= 3) {
                            this.log.info(`detected configuration change`);
                            this.isDetectionNeeded = true;
                            this.framework.changePhase('configuring');
                        }
                    }
                    else {
                        this.pushControllersMismatchCount = 0;
                    }
                }),
            },
        ];
    }
    // phase callbacks
    onConfigure(config) {
        return __awaiter(this, void 0, void 0, function* () {
            this.timeouts.clearAll();
            this.mutePresets = {};
            this.nonPushControllers.length = 0;
            this.pushControllersMismatchCount = 0;
            this.telephonyChannels.forEach(tc => {
                delete tc.callerId;
                delete tc.config;
                delete tc.numberDialed;
                delete tc.isValid;
            });
            this.unmutePresets = {};
        });
    }
    onControl() {
        return __awaiter(this, void 0, void 0, function* () {
            // Refresh all push data, effectively querying everything. This isn't necessary for mutes
            // and volumes since they are already queried individually. However, it is a much simpler
            // way of querying the telephony parameters than doing it manually.
            yield this.socket.sendAsync(`PUR`, ['control']);
        });
    }
    onDisconnect() {
        return __awaiter(this, void 0, void 0, function* () {
            this.timeouts.clearAll();
        });
    }
    onInitialize(credentials) {
        return __awaiter(this, void 0, void 0, function* () {
            return CommandSocketInitializer.initialize(this.socket, [
                {
                    errorCode: 'verifyFailed',
                    send: 'FU 5',
                    succeed: /^ACK$/,
                },
            ], credentials);
        });
    }
    // other class methods
    addController(controllers, controller) {
        this.removeController(controllers, controller.number);
        controllers.push(controller);
    }
    addTelephonyChannel(unit, card, line) {
        this.removeTelephonyChannel(unit, card, line);
        this.telephonyChannels.push({ unit, card, line });
    }
    canGet(controllerNumber, tags) {
        return __awaiter(this, void 0, void 0, function* () {
            return (yield this.getControllerValue(controllerNumber, tags)) !== undefined;
        });
    }
    dialDigits(config, digits, tags = []) {
        return __awaiter(this, void 0, void 0, function* () {
            yield digits.split('').reduce((promise, digit) => __awaiter(this, void 0, void 0, function* () {
                yield promise;
                if (digit === '*')
                    digit = 'Star';
                if (digit === '#')
                    digit = 'Pound';
                const cn = config[`keypad${digit}`];
                yield this.socket.send(`CSQ ${cn} 65535`, /^(ACK|NAK)$/, tags);
            }), Promise.resolve());
        });
    }
    getAvailableTelephonyChannel(callType) {
        return this.telephonyChannels.find(tc => {
            var _a, _b;
            if (!tc.isValid || ((_a = tc.config) === null || _a === void 0 ? void 0 : _a.callType) !== callType)
                return false;
            const key = this.getTelephonyKey(tc.config);
            return ((_b = this.state.calls.values[key]) === null || _b === void 0 ? void 0 : _b.callState) === 'disconnected';
        });
    }
    getController(controllers, number) {
        return controllers.find(controller => controller.number === number);
    }
    getControllerValue(controllerNumber, tags) {
        return __awaiter(this, void 0, void 0, function* () {
            // We have seen the Radius sometimes return a NAK followed by a push result for a valid
            // controller query. To work around this bug, we query a few times just to be sure.
            return [0, 1, 2].reduce((promise, i) => __awaiter(this, void 0, void 0, function* () {
                const value = yield promise;
                if (value !== undefined)
                    return value;
                const match = yield this.socket.send(`GS2 ${controllerNumber}`, new RegExp(`^(?:0*${controllerNumber}\\s+(\\d+)|NAK)$`), tags);
                return match[1] !== undefined ? parseInt(match[1], 10) : undefined;
            }), Promise.resolve(undefined));
        });
    }
    getKey(config) {
        if (isPresetMuteConfig(config)) {
            return this.getKey(config.muteStatus);
        }
        return config.toString();
    }
    getTelephonyChannel(unitOrKey, card, line) {
        let unit = 0;
        if (typeof unitOrKey === 'number') {
            unit = unitOrKey;
        }
        else {
            [unit, card, line] = unitOrKey.split('.').map(s => parseInt(s, 10));
        }
        return this.telephonyChannels.find(tc => tc.unit === unit && tc.card === card && tc.line === line);
    }
    getTelephonyKey(config) {
        if (config === undefined)
            return 'undefined';
        return `${config.unit}.${config.card}.${config.line}`;
    }
    processCallControl(controller, value) {
        var _a;
        const cn = controller.number;
        for (const tc of this.telephonyChannels) {
            const config = tc.config;
            if (config === undefined)
                continue;
            const key = this.getTelephonyKey(tc.config);
            if ('ringingLed' in config && config.ringingLed === cn) {
                if (value > 0) {
                    this.state.calls.setIncoming(key);
                }
                else {
                    if (((_a = this.state.calls.values[key]) === null || _a === void 0 ? void 0 : _a.callState) === 'ringing') {
                        this.state.calls.disconnect(key);
                    }
                }
            }
            else if ('connectedLed' in config && config.connectedLed === cn) {
                if (value > 0) {
                    this.state.calls.connect(key);
                    if (tc.numberDialed !== undefined) {
                        this.state.calls.updateCall(key, { dialString: tc.numberDialed });
                    }
                }
                else {
                    this.state.calls.disconnect(key);
                }
                delete tc.numberDialed;
            }
            else if ('receiveGain' in config && config.receiveGain === cn) {
                this.state.calls.updateReceiveGain(key, value);
            }
        }
    }
    processCallString(unitStr, resourceStr, cardStr, lineStr, value) {
        var _a;
        const unit = parseInt(unitStr, 10);
        const resource = parseInt(resourceStr, 10);
        const card = parseInt(cardStr, 10);
        const line = parseInt(lineStr, 10);
        // only handle caller id and voip status
        if (resource !== Resource.CallerId && resource !== Resource.CallStatus)
            return;
        const tc = this.getTelephonyChannel(unit, card, line);
        if (tc === undefined || tc.config === undefined)
            return;
        const key = this.getTelephonyKey(tc.config);
        if (resource === Resource.CallerId) {
            if (((_a = this.state.calls.values[key]) === null || _a === void 0 ? void 0 : _a.callState) !== 'disconnected') {
                this.state.calls.updateCall(key, { contactName: value });
            }
            else {
                tc.callerId = { contactName: value };
            }
        }
        else if (resource === Resource.CallStatus) {
            if (tc.config.callType !== 'voip') {
                this.log.error(`got call status for ${tc.config.callType} call`);
                return;
            }
            const stateMatch = /^([\w\s]+)/.exec(value);
            if (!stateMatch) {
                this.log.error(`invalid call status: ${value}`);
                return;
            }
            switch (stateMatch[1].toLowerCase()) {
                case 'busy':
                case 'dialing':
                    this.state.calls.setOutgoing(key);
                    if (tc.numberDialed !== undefined) {
                        this.state.calls.updateCall(key, { dialString: tc.numberDialed });
                        delete tc.numberDialed;
                    }
                    break;
                case 'in call':
                    if (tc.numberDialed !== undefined) {
                        this.state.calls.updateCall(key, { dialString: tc.numberDialed });
                        delete tc.numberDialed;
                    }
                // fall through
                case 'hold':
                case 'unavailable':
                case 'remote hold':
                    this.state.calls.connect(key);
                    break;
                case 'idle':
                case 'ready':
                    this.state.calls.disconnect(key);
                    delete tc.callerId;
                    delete tc.numberDialed;
                    break;
                case 'ringing':
                    this.state.calls.setIncoming(key, tc.callerId);
                    delete tc.callerId;
                    break;
                default:
                    this.log.error(`unexpected call status: ${value}`);
                    break;
            }
        }
    }
    removeController(controllers, number) {
        Sutl.filterInPlace(controllers, controller => controller.number !== number);
    }
    removeTelephonyChannel(unit, card, line) {
        Sutl.filterInPlace(this.telephonyChannels, tc => tc.unit !== unit || tc.card !== card || tc.line !== line);
    }
    validate(config, info) {
        return __awaiter(this, void 0, void 0, function* () {
            const key = this.getKey(config);
            let controllerNumber;
            if (isPresetMuteConfig(config)) {
                this.mutePresets[key] = config.mutePreset;
                this.unmutePresets[key] = config.unmutePreset;
                controllerNumber = config.muteStatus;
            }
            else {
                controllerNumber = config;
            }
            const controller = this.getController(this.pushControllers, controllerNumber);
            if (controller) {
                // Validate volume mute, but only write the controller properties for volume.
                if (info.filter !== 'amplifier' || info.type !== 'mute') {
                    controller.filter = info.filter;
                    controller[info.type === 'mute' ? 'isMute' : 'isVolume'] = true;
                }
                return controller.canGet ? 'ok' : 'incompatible';
            }
            else {
                const canGet = yield this.canGet(controllerNumber, ['validate']);
                if (canGet) {
                    this.addController(this.nonPushControllers, {
                        number: controllerNumber,
                        canGet: true,
                    });
                    // For now, return incompatible. We could allow controllers without push enabled,
                    // but we would have to poll them.
                    return 'incompatible';
                }
                return 'incompatible';
            }
        });
    }
    validateTelephonyControlPoint(config, info) {
        return __awaiter(this, void 0, void 0, function* () {
            const controllerNumber = config[info.key];
            if (controllerNumber === undefined || typeof controllerNumber !== 'number') {
                return 'missing';
            }
            let controller = this.getController(this.pushControllers, controllerNumber);
            if (controller !== undefined) {
                controller.filter = 'telephony';
                return 'ok';
            }
            if (info.isPushRequired)
                return 'incompatible';
            controller = this.getController(this.nonPushControllers, controllerNumber);
            if (controller !== undefined)
                return 'ok';
            const canGet = yield this.canGet(controllerNumber, ['validate']);
            if (canGet) {
                this.addController(this.nonPushControllers, {
                    number: controllerNumber,
                    canGet: true,
                });
                return 'ok';
            }
            return 'missing';
        });
    }
}
// static properties
Radius.channelsConfigName = 'radiusControlChannels';
Radius.driverType = 'socketDriver';
Radius.key = '-L-7rxdze1CkSjtklwbg';
Radius.socketOptions = [
    { port: 48631, sendTimeoutMs: 3500 },
];
